<template>
  <!-- AI AutoPilot's chat (Workspace369 lib/view/global_search/ask_ai/), drawn from the shipped
       components in the app's Ask AI design lab (lib/view/debug/ask_ai_design_lab, "Current"):
       the home greeting and shortcuts, the Activity card (squared checks on a rail, "3 of 3
       completed"), the approval card and a reply with a record table. Labels are the app's own
       strings (lib/l10n/app_en.arb: autopilotChat*, askAi*; step names from services/function_label_map.dart; the approval card follows widgets/pending_action_card.dart); clients and amounts are sample data.
       Four screens share one fixed box and cross-fade on `state` (ask → work → approve → done), so
       nothing outside the window moves. The server renders `done`, the payoff. -->
  <div class="acw" :class="[`acw--${state}`, { 'acw--compact': compact }]" aria-hidden="true">
    <header class="acw-head">
      <span class="acw-title">
        <svg viewBox="0 0 24 24"><path :d="icons.sparkle" /></svg>
        <span>
          <b translate="no">AI AutoPilot</b>
          <small>Ask me anything</small>
        </span>
      </span>
      <span class="acw-head-btn"><svg viewBox="0 0 24 24"><path :d="icons.plus" /></svg>New chat</span>
    </header>

    <div class="acw-screens">
      <!-- 1. Home: the greeting, the default shortcuts and the question being typed. -->
      <section class="acw-screen acw-s-ask">
        <p class="acw-greet">What are we working on today?</p>
        <p class="acw-greet-sub">Describe a task below — AutoPilot works on it right here.</p>
        <div class="acw-shortcuts">
          <span v-for="s in shortcuts" :key="s.label" class="acw-chip">
            <svg viewBox="0 0 24 24"><path :d="icons[s.icon]" /></svg>{{ s.label }}
          </span>
        </div>
      </section>

      <!-- 2. Working: the question, then the Activity card with steps ticking through. -->
      <section class="acw-screen acw-s-work">
        <p class="acw-user">Find overdue invoices and email each client a reminder</p>
        <div class="acw-card">
          <div class="acw-card-head">
            <b>Activity</b>
            <span class="acw-pill acw-pill--run"><i class="acw-spin" />Working...</span>
          </div>
          <small class="acw-card-sub">1 of 3 completed</small>
          <ol class="acw-steps">
            <li class="is-done"><span class="acw-box"><svg viewBox="0 0 24 24"><path :d="icons.check" /></svg></span><span><b>Finding invoices</b><small>2 overdue · $3,650.00</small></span></li>
            <li class="is-doing"><span class="acw-box"><i class="acw-spin" /></span><span><b>Reading invoice</b><small>Acme Co · INV-1042</small></span></li>
            <li><span class="acw-box" /><span><b>Reading invoice</b><small>Northwind Studio · INV-1038</small></span></li>
          </ol>
        </div>
      </section>

      <!-- 3. Approval: a send to a client always stops here (askAiAutoModeSendsStillAsk). -->
      <section class="acw-screen acw-s-approve">
        <p class="acw-ai">Both invoices are ready to email again. A send to a client always waits for you.</p>
        <div class="acw-card acw-card--approve">
          <div class="acw-card-head">
            <b>Send invoice INV-1042</b>
            <span class="acw-pill"><svg viewBox="0 0 24 24"><path :d="icons.pause" /></svg>Needs approval</span>
          </div>
          <small class="acw-card-sub">Email this invoice to the recipient shown below.</small>
          <dl class="acw-fields">
            <div><dt>Client</dt><dd>Acme Co</dd></div>
            <div><dt>Amount</dt><dd>$2,450.00</dd></div>
            <div><dt>Recipient</dt><dd>billing@acme.test</dd></div>
            <div><dt>Due</dt><dd>Oct 9, 2026</dd></div>
          </dl>
          <div class="acw-actions">
            <span class="acw-btn-quiet">Cancel</span>
            <span class="acw-btn">Send Email</span>
          </div>
        </div>
      </section>

      <!-- 4. Done: the reply, a table whose rows open the records, and the finished Activity. -->
      <section class="acw-screen acw-s-done">
        <p class="acw-ai">Two clients had overdue invoices, <b>$3,650.00</b> in all. You approved the email to Acme Co; Northwind Studio's is next, and waits for you too.</p>
        <div class="acw-table">
          <div class="acw-tr acw-th"><span>Client</span><span>Invoice</span><span class="acw-num">Amount</span><span>Status</span></div>
          <div v-for="row in rows" :key="row.invoice" class="acw-tr">
            <span class="acw-strong">{{ row.client }}</span>
            <span class="acw-link">{{ row.invoice }}</span>
            <span class="acw-num">{{ row.amount }}</span>
            <span><em class="acw-tag" :class="`acw-tag--${row.tone}`">{{ row.status }}</em></span>
          </div>
        </div>
        <div class="acw-card acw-card--slim">
          <div class="acw-card-head">
            <b>Activity</b>
            <span class="acw-pill acw-pill--ok"><svg viewBox="0 0 24 24"><path :d="icons.check" /></svg>Completed</span>
          </div>
          <small class="acw-card-sub">3 of 3 completed</small>
        </div>
      </section>
    </div>

    <!-- The composer stays put; on the home screen the question types into it. -->
    <div class="acw-composer">
      <p class="acw-typed">
        <span class="acw-hint">Message AI AutoPilot... (@ to mention)</span>
        <span class="acw-question"><span class="acw-caret-text">Find overdue invoices and email each client a reminder</span><i class="acw-caret" /></span>
      </p>
      <div class="acw-composer-row">
        <span class="acw-icon-btn"><svg viewBox="0 0 24 24"><path :d="icons.plus" /></svg></span>
        <span class="acw-icon-btn">@</span>
        <span class="acw-effort"><svg viewBox="0 0 24 24"><path :d="icons.sparkle" /></svg>Auto</span>
        <span class="acw-send"><svg viewBox="0 0 24 24"><path :d="icons.up" /></svg></span>
      </div>
    </div>
  </div>
</template>

<script setup>
defineProps({
  // ask | work | approve | done
  state: { type: String, default: "done" },
  // Smaller type for narrow stages.
  compact: { type: Boolean, default: false },
});

// The app's default shortcuts (autopilotChatShortcut*).
const shortcuts = [
  { label: "Plan my day", icon: "calendar" },
  { label: "Draft an invoice", icon: "receipt" },
  { label: "Review my workspace", icon: "search" },
];
const rows = [
  { client: "Acme Co", invoice: "INV-1042", amount: "$2,450.00", status: "Sent", tone: "ok" },
  { client: "Northwind Studio", invoice: "INV-1038", amount: "$1,200.00", status: "Overdue", tone: "muted" },
];
const icons = {
  sparkle: "M19 9l1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25L19 9zm-7.5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12l-5.5-2.5zM19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25L19 15z",
  plus: "M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z",
  check: "M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z",
  pause: "M6 19h4V5H6v14zm8-14v14h4V5h-4z",
  up: "M4 12l1.41 1.41L11 7.83V20h2V7.83l5.58 5.59L20 12l-8-8-8 8z",
  calendar: "M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-2 .9-2 2v14a2 2 0 0 0 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z",
  receipt: "M18 17H6v-2h12v2zm0-4H6v-2h12v2zm0-4H6V7h12v2zM3 22l1.5-1.5L6 22l1.5-1.5L9 22l1.5-1.5L12 22l1.5-1.5L15 22l1.5-1.5L18 22l1.5-1.5L21 22V2l-1.5 1.5L18 2l-1.5 1.5L15 2l-1.5 1.5L12 2l-1.5 1.5L9 2 7.5 3.5 6 2 4.5 3.5 3 2v20z",
  search: "M15.5 14h-.79l-.28-.27A6.47 6.47 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z",
};
</script>

<style>
/* App tokens: the shell's page colours, hairlines at onSurface 12%, Space Grotesk, the black
   primary button and the AppTone green (lib/view/global_search/ask_ai/widgets/ask_ai_pro_style.dart). */
.acw {
  --acw-page: #fff;
  --acw-wash: #f4f6f8;
  --acw-card: #fff;
  --acw-line: rgb(0 0 0 / 0.1);
  --acw-text: #171717;
  --acw-muted: #5f6368;
  --acw-faint: #8a8f96;
  --acw-ink: #171717;
  --acw-ink-text: #fff;
  --acw-bubble: #ececec;
  --acw-green: #2d7a4e;
  --acw-green-fill: #e6f4ea;
  --acw-link: #3f58bf;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  border: 1px solid var(--acw-line);
  border-radius: 10px;
  background: var(--acw-wash);
  color: var(--acw-text);
  font-family: "Space Grotesk", Inter, ui-sans-serif, system-ui, sans-serif;
  font-size: 13px;
  line-height: 1.4;
  letter-spacing: 0;
  box-shadow: 0 18px 50px -18px rgb(15 23 42 / 0.28), 0 2px 6px rgb(15 23 42 / 0.06);
  text-align: left;
}
.dark .acw,
.acw-dark .acw {
  --acw-page: #1c1c1c;
  --acw-wash: #242424;
  --acw-card: #262626;
  --acw-line: rgb(255 255 255 / 0.13);
  --acw-text: #ececec;
  --acw-muted: #a8a8a8;
  --acw-faint: #8a8a8a;
  --acw-ink: #ececec;
  --acw-ink-text: #171717;
  --acw-bubble: #2f2f2f;
  --acw-green: #6ed39a;
  --acw-green-fill: rgb(63 178 110 / 0.18);
  --acw-link: #93aef9;
  box-shadow: 0 18px 50px -18px rgb(0 0 0 / 0.7);
}
.acw svg { width: 14px; height: 14px; flex: none; fill: currentColor; }
.acw-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border-bottom: 1px solid var(--acw-line);
  background: var(--acw-page);
  padding: 10px 14px;
}
.acw-title { display: flex; align-items: center; gap: 8px; }
.acw-title svg { width: 16px; height: 16px; }
.acw-title > span { display: grid; line-height: 1.2; }
.acw-title b { font-size: 14px; font-weight: 600; }
.acw-title small { color: var(--acw-muted); font-size: 11px; }
.acw-head-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 6px;
  background: var(--acw-wash);
  padding: 5px 9px;
  font-size: 12px;
  font-weight: 500;
}
.acw-head-btn svg { width: 13px; height: 13px; }

/* The four screens stack in one cell; only the current one is shown. */
.acw-screens { display: grid; flex: 1; min-height: 0; }
.acw-screen {
  display: flex;
  grid-area: 1 / 1;
  flex-direction: column;
  gap: 10px;
  min-height: 0;
  padding: 16px;
  opacity: 0;
  visibility: hidden;
  transform: translateY(10px);
  transition: opacity 0.38s ease, transform 0.5s cubic-bezier(0.33, 1, 0.68, 1), visibility 0s linear 0.4s;
}
.acw--ask .acw-s-ask,
.acw--work .acw-s-work,
.acw--approve .acw-s-approve,
.acw--done .acw-s-done {
  opacity: 1;
  visibility: visible;
  transform: none;
  transition: opacity 0.42s ease 0.08s, transform 0.55s cubic-bezier(0.33, 1, 0.68, 1) 0.08s, visibility 0s;
}

/* Home */
.acw-s-ask { justify-content: center; }
.acw-greet { margin: 0; font-size: 19px; font-weight: 600; line-height: 1.2; }
.acw-greet-sub { margin: -4px 0 4px; color: var(--acw-muted); font-size: 12px; }
.acw-shortcuts { display: flex; flex-wrap: wrap; gap: 6px; }
.acw-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid var(--acw-line);
  border-radius: 6px;
  background: var(--acw-card);
  padding: 5px 9px;
  font-size: 12px;
}
.acw-composer {
  flex: none;
  margin: 0 12px 12px;
  border: 1px solid var(--acw-line);
  border-radius: 10px;
  background: var(--acw-card);
  padding: 9px 10px 6px 12px;
}
.acw-typed { display: grid; min-height: 20px; margin: 0; font-size: 13px; }
.acw-typed > span { grid-area: 1 / 1; transition: opacity 0.3s ease; }
.acw-hint { color: var(--acw-faint); }
.acw-question { opacity: 0; }
.acw--ask .acw-hint { opacity: 0; }
.acw--ask .acw-question { opacity: 1; }
.acw-caret {
  display: inline-block;
  width: 1.5px;
  height: 15px;
  margin-left: 1px;
  background: var(--acw-text);
  vertical-align: -2px;
  animation: acw-blink 1s steps(1) infinite;
}
@keyframes acw-blink { 50% { opacity: 0; } }
/* The question types itself each time the home screen comes in. */
.acw--ask .acw-caret-text { animation: acw-type 1.5s steps(40, end) 0.35s both; }
@keyframes acw-type { from { clip-path: inset(0 100% 0 0); } to { clip-path: inset(0); } }
.acw-composer-row { display: flex; align-items: center; gap: 4px; margin-top: 4px; color: var(--acw-muted); }
.acw-icon-btn { display: grid; place-items: center; width: 26px; height: 26px; font-size: 14px; font-weight: 600; }
.acw-effort { display: inline-flex; align-items: center; gap: 4px; margin-left: 4px; font-size: 12px; }
.acw-send {
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  margin-left: auto;
  border-radius: 999px;
  background: var(--acw-ink);
  color: var(--acw-ink-text);
}

/* Messages */
.acw-user {
  align-self: flex-end;
  max-width: 86%;
  margin: 0;
  border-radius: 10px;
  background: var(--acw-bubble);
  padding: 8px 11px;
}
.acw-ai { margin: 0; max-width: 96%; }
.acw-ai b { font-weight: 600; font-variant-numeric: tabular-nums; }

/* Activity and approval cards (AskAiPro.blockRadius 11, hairline, no shadow) */
.acw-card {
  border: 1px solid var(--acw-line);
  border-radius: 11px;
  background: var(--acw-card);
  padding: 12px 14px;
}
.acw-card-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.acw-card-head b { font-weight: 600; }
.acw-card-sub { display: block; margin-top: 2px; color: var(--acw-muted); font-size: 11.5px; }
.acw-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: none;
  border-radius: 6px;
  background: var(--acw-wash);
  padding: 2px 7px;
  color: var(--acw-muted);
  font-size: 11.5px;
  white-space: nowrap;
}
.acw-pill svg { width: 12px; height: 12px; }
.acw-pill--ok { background: var(--acw-green-fill); color: var(--acw-green); }
.acw-steps { display: grid; gap: 9px; margin: 10px 0 0; padding: 0; list-style: none; }
.acw-steps li { position: relative; display: flex; gap: 10px; }
.acw-steps li + li::before {
  content: "";
  position: absolute;
  top: -9px;
  left: 8.5px;
  width: 1px;
  height: 9px;
  background: var(--acw-line);
}
.acw-steps li > span:last-child { display: grid; line-height: 1.3; }
.acw-steps b { font-weight: 500; }
.acw-steps small { color: var(--acw-muted); font-size: 11.5px; }
.acw-steps li:not(.is-done):not(.is-doing) { color: var(--acw-faint); }
.acw-box {
  display: grid;
  place-items: center;
  flex: none;
  width: 18px;
  height: 18px;
  border: 1px solid var(--acw-line);
  border-radius: 5px;
  background: var(--acw-wash);
}
.acw-box svg { width: 12px; height: 12px; }
.acw-spin {
  width: 10px;
  height: 10px;
  border: 1.5px solid var(--acw-line);
  border-top-color: var(--acw-text);
  border-radius: 999px;
  animation: acw-spin 0.8s linear infinite;
}
@keyframes acw-spin { to { transform: rotate(360deg); } }
/* Steps tick through while the working screen is up. */
.acw--work .acw-steps li.is-doing .acw-box { animation: acw-pop 0.3s ease 1.6s both; }
@keyframes acw-pop { from { transform: scale(0.85); } to { transform: none; } }

.acw-fields { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 14px; margin: 12px 0 0; }
.acw-fields dt { color: var(--acw-muted); font-size: 11.5px; }
.acw-fields dd { margin: 1px 0 0; overflow: hidden; font-weight: 500; text-overflow: ellipsis; white-space: nowrap; }
.acw-actions { display: flex; align-items: center; justify-content: flex-end; gap: 14px; margin-top: 12px; }
.acw-btn-quiet { font-weight: 500; }
.acw-btn {
  position: relative;
  border-radius: 7px;
  background: var(--acw-ink);
  padding: 8px 14px;
  color: var(--acw-ink-text);
  font-weight: 600;
}
/* The approval: a ring pulses on the primary button once the card is up. */
.acw--approve .acw-btn::after {
  content: "";
  position: absolute;
  inset: -4px;
  border: 1.5px solid #1ba7ce;
  border-radius: 10px;
  opacity: 0;
  animation: acw-ring 1.4s ease-out 0.9s 2 both;
}
@keyframes acw-ring { 0% { opacity: 0; transform: scale(0.96); } 40% { opacity: 1; } 100% { opacity: 0; transform: scale(1.06); } }

/* Reply table (ask_ai_markdown_table.dart): header row, hairlines, record links. */
.acw-table { overflow: hidden; border: 1px solid var(--acw-line); border-radius: 10px; background: var(--acw-card); }
.acw-tr { display: grid; grid-template-columns: 1.35fr 1fr 0.95fr 0.7fr; align-items: center; gap: 8px; padding: 8px 12px; }
.acw-tr + .acw-tr { border-top: 1px solid var(--acw-line); }
.acw-th { color: var(--acw-muted); font-size: 11.5px; font-weight: 500; }
.acw-num { text-align: right; font-variant-numeric: tabular-nums; }
.acw-strong { overflow: hidden; font-weight: 500; text-overflow: ellipsis; white-space: nowrap; }
.acw-link { color: var(--acw-link); text-decoration: underline; text-decoration-thickness: 1px; text-underline-offset: 2px; }
.acw-tag { border-radius: 5px; padding: 1px 6px; font-size: 11.5px; font-style: normal; }
.acw-tag--ok { background: var(--acw-green-fill); color: var(--acw-green); }
.acw-tag--muted { background: var(--acw-wash); color: var(--acw-muted); }
.acw-card--slim { padding: 9px 14px; }
.acw--done .acw-table .acw-tr:not(.acw-th) { animation: acw-row 0.5s cubic-bezier(0.33, 1, 0.68, 1) both; }
.acw--done .acw-table .acw-tr:nth-child(2) { animation-delay: 0.25s; }
.acw--done .acw-table .acw-tr:nth-child(3) { animation-delay: 0.4s; }
@keyframes acw-row { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }

.acw--compact { font-size: 12px; }
.acw--compact .acw-greet { font-size: 17px; }
.acw-screen > * { flex-shrink: 0; }
@media (max-width: 480px) {
  .acw { font-size: 12px; }
  /* Phones: the finished Activity line makes way for the table. */
  .acw-card--slim { display: none; }
  .acw-screen { padding: 12px; }
  .acw-tr { grid-template-columns: 1.2fr 0.9fr 0.75fr; }
  .acw-tr > :nth-child(3) { display: none; }
  .acw-fields { grid-template-columns: 1fr; gap: 6px; }
  .acw-fields > div:nth-child(4) { display: none; }
}
@media (prefers-reduced-motion: reduce) {
  .acw *, .acw-screen { animation: none !important; transition: none !important; }
}
</style>
